import React, { Component } from 'react';

import {
  Divider,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  TableView,
  Text,
  VStack,
  Checkbox,
  ScrollPane,
} from '../../core-ui';
import { SortBy } from '../../core-ui/TableView';
import { confirm, showDialog } from '../App/AppDialog';

import DataStore from '../../support/DataStore';
import { viewAsSeason } from '../../support/Season';
import { fetchJSON, postJSON } from '../../support/Fetch';
import formatDate from '../../core/support/formatDate';
import { canUser } from '../../support/shared/permissionHelpers';

import { Season } from '../../types/shared/Season';
import { useNavigation } from '../Navigation';
import { TbDotsVertical, TbFileZip, TbPencil, TbTrash } from 'react-icons/tb';
import { IconType } from 'react-icons';
import { useAuthState } from '../AuthState';

type State = {
  seasons: Array<Season>;
  sortBy: SortBy;
};

const dataStore = DataStore.getInstance('app');

type Props = {
  isSystemAdmin: boolean;
  onAfterSeasonChange: () => void;
};

class SeasonsListView extends Component<Props, State> {
  state: State = {
    seasons: [],
    sortBy: [1, true], // Sort by most recent season
  };

  componentDidMount() {
    void this._fetchSeasons();
  }

  render() {
    const { seasons, sortBy } = this.state;
    const currentSeasonId = seasons[0]?.id ?? '';
    const currentlyViewedSeasonId =
      dataStore.get('season')?.id ?? currentSeasonId;
    return (
      <VStack px={10} pt={10} flex={1} minH={0} align="stretch" spacing={2}>
        <HStack>
          <Heading as="h2" size="lg">
            {t('Seasons')}
          </Heading>
        </HStack>
        <Divider borderColor="gray.200" />
        <ScrollPane contentContainerProps={{ flexGrow: 1, pb: 10 }}>
          <TableView
            data={seasons}
            onSortChange={(sortBy) => this.setState({ sortBy })}
            sortBy={sortBy}
            onRowClick={(season) => this._showSeason(season)}
            isRowClickable={(season) => season.id !== currentlyViewedSeasonId}
            columns={[
              {
                title: t('Season'),
                render: (season) => season.name,
                sortable: {
                  sortFn: (a, b) => a.name.localeCompare(b.name),
                },
              },
              {
                title: t('Created At'),
                render: (season) => formatDate(season.createdAt),
                sortable: {
                  sortFn: (a, b) =>
                    Date.parse(a.createdAt) - Date.parse(b.createdAt),
                },
              },
              {
                title: 'Status',
                render: (season) => {
                  const status =
                    season.id === currentSeasonId
                      ? isDraft(season)
                        ? t('Current Season (draft)')
                        : t('Current Season')
                      : t('Archived');
                  return <Text>{status}</Text>;
                },
              },
              {
                title: '',
                tdProps: {
                  width: '1%',
                  cursor: 'default',
                  onClick: (event) => event.stopPropagation(),
                },
                render: (season) => this._renderActionMenu(season),
              },
            ]}
          />
        </ScrollPane>
      </VStack>
    );
  }

  _renderActionMenu = (season: Season) => {
    const { seasons } = this.state;
    const user = dataStore.get('user');
    const currentSeasonId = seasons[0]?.id ?? '';
    const currentlyViewedSeasonId =
      dataStore.get('season')?.id ?? currentSeasonId;
    const { id } = season;
    const renderIcon = (icon: IconType) => {
      return <Icon as={icon} fontSize={16} mt={1} />;
    };
    const canEdit = canUser(user, { settings: 'WRITE' });
    const canArchive = canEdit && currentSeasonId === id;
    const canDelete =
      canEdit && id !== currentSeasonId && id !== currentlyViewedSeasonId;
    if (!canEdit && !canArchive && !canDelete) {
      return null;
    }
    return (
      <Menu>
        <MenuButton
          as={IconButton}
          aria-label={t('Actions')}
          icon={<Icon as={TbDotsVertical} fontSize={16} />}
        />
        <Portal>
          <MenuList>
            {canEdit ? (
              <MenuItem
                align="center"
                icon={renderIcon(TbPencil)}
                onClick={() => this._renameSeason(season)}
              >
                {t('Edit')}
              </MenuItem>
            ) : null}
            {canArchive ? (
              <MenuItem
                align="center"
                icon={renderIcon(TbFileZip)}
                onClick={() => this._archiveSeason()}
              >
                {t('Archive Now')}
              </MenuItem>
            ) : null}
            {canDelete ? (
              <MenuItem
                align="center"
                icon={renderIcon(TbTrash)}
                onClick={() => this._deleteSeason(id)}
              >
                {t('Delete')}
              </MenuItem>
            ) : null}
          </MenuList>
        </Portal>
      </Menu>
    );
  };

  _showSeason = (season: Season) => {
    const { seasons } = this.state;
    const currentSeasonId = seasons[0]?.id ?? '';
    if (season.id === currentSeasonId) {
      viewAsSeason(null);
    } else {
      viewAsSeason(season);
    }
    this.props.onAfterSeasonChange();
  };

  _renameSeason = (season: Season) => {
    const { isSystemAdmin } = this.props;
    const { seasons } = this.state;
    const currentSeasonId = seasons[0]?.id ?? '';
    const isCurrentSeason = season.id === currentSeasonId;
    const SAVE = t('Save');
    showDialog(
      t('Edit Season'),
      () => {
        const name = season ? season.name : '';
        return (
          <VStack align="stretch">
            <Text>{t('Name')}</Text>
            <Input name="seasonName" defaultValue={name} autoFocus />
            <Checkbox
              hidden={!isCurrentSeason || !isSystemAdmin}
              name="draft"
              value="true"
              defaultChecked={isCurrentSeason && isDraft(season)}
            >
              {t('Draft Season')}
            </Checkbox>
          </VStack>
        );
      },
      [t('Cancel'), SAVE],
      async (button, data) => {
        if (button !== SAVE) {
          return;
        }
        // TODO: Error Handling
        await postJSON(`/api/seasons/${season.id}`, {
          name: String(data.seasonName),
          draft: data.draft === 'true',
        });
        await this._fetchSeasons();
      },
      { noAutoFocus: true },
    );
  };

  _archiveSeason = async () => {
    const { isSystemAdmin } = this.props;
    const OK = t('OK');
    const CANCEL = t('Cancel');
    showDialog(
      t('Archive this Season?'),
      () => {
        return (
          <VStack align="stretch">
            <Text>
              {t('Are you sure you want to archive the current season?')}
            </Text>
            <Checkbox
              hidden={!isSystemAdmin}
              name="draft"
              value="true"
              defaultChecked={false}
            >
              {t('New season as draft')}
            </Checkbox>
          </VStack>
        );
      },
      [CANCEL, OK],
      async (button, data) => {
        if (button === CANCEL) {
          return;
        }
        // TODO: Error Handling
        await postJSON('/api/seasons', {
          draft: data.draft === 'true',
        });
        await this._fetchSeasons();
      },
      { noAutoFocus: true },
    );
  };

  _deleteSeason = async (id: string) => {
    const proceed = await confirm(
      t('Permanently Delete Season?'),
      t(
        'Are you sure you want to delete this season including all vendors/applicants?',
      ),
    );
    if (!proceed) {
      return;
    }
    const data = await postJSON(`/api/seasons/${id}/delete`, {});
    this.setState({ seasons: data.seasons.reverse() });
  };

  _fetchSeasons = async () => {
    const data = await fetchJSON('/api/seasons');
    if (data != null && Array.isArray(data.seasons)) {
      this.setState({ seasons: data.seasons.reverse() });
    }
  };
}

function isDraft(season: Season) {
  return Object(season).draft === true;
}

export default function SeasonsPage() {
  const { navigate } = useNavigation();
  const { account } = useAuthState();
  return (
    <SeasonsListView
      isSystemAdmin={account.system_admin === true}
      onAfterSeasonChange={() => {
        navigate('/vendors');
      }}
    />
  );
}
